import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const payoutControlSlice = createSlice({
    name: "payoutControl",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        listData: null,
        statsData: null,
        detailData: null,
        exporting: false,
        processSuccess: false,
        errorDetailData: null,
    },
    reducers: {
        doGetPayoutListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.totalRow = action.payload.total_row;
        },
        doGetPayoutListFail: (state) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        doGetPayoutStatsSuccess: (state, action) => {
            state.statsData = action.payload;
        },
        doGetPayoutStatsFail: (state) => {
            state.statsData = null;
        },
        doGetPayoutDetailSuccess: (state, action) => {
            state.detailData = action.payload.payout;
        },
        doGetPayoutDetailFail: (state) => {
            state.detailData = null;
        },
        doMarkProcessedSuccess: (state) => {
            state.processSuccess = true;
        },
        doMarkProcessedFail: (state, action) => {
            state.processSuccess = false;
            state.errorDetailData = action.payload;
        },
        setPayoutExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetPayoutControlState: (state) => {
            state.processSuccess = false;
            state.errorDetailData = null;
        },
        resetPayoutControlDetailState: (state) => {
            state.detailData = null;
            state.processSuccess = false;
            state.errorDetailData = null;
        },
    },
});

const buildPayoutListUrl = (param) => {
    let url = `/api/admin/payout-control?page=${param.page}&size=${param.limit}`;
    if (param.keyword) url += `&keyword=${encodeURIComponent(param.keyword)}`;
    if (param.status) url += `&status=${param.status}`;
    if (param.payout_status) url += `&payout_status=${param.payout_status}`;
    if (param.date_from) url += `&date_from=${param.date_from}`;
    if (param.date_to) url += `&date_to=${param.date_to}`;
    if (param.sort_by) url += `&sort_by=${param.sort_by}`;
    return url;
};

export const doGetPayoutList = (param) => async (dispatch) => {
    try {
        const response = await axios.get(buildPayoutListUrl(param));
        dispatch(doGetPayoutListSuccess(response.data));
    } catch (error) {
        dispatch(doGetPayoutListFail(error.response));
    }
};

export const doGetPayoutStats = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/payout-control/stats`);
        dispatch(doGetPayoutStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetPayoutStatsFail(error.response));
    }
};

// ---- CSV export --------------------------------------------------------
// There is no server-side export endpoint, so the whole filtered set is pulled
// through the list endpoint (which caps `size` at 100) and the CSV is built in
// the browser — same approach as the deal monitoring export.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;

const csvDate = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d)) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export const doExportPayoutControl = (param) => async (dispatch) => {
    try {
        dispatch(setPayoutExporting(true));

        const rows = [];
        for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
            const url = buildPayoutListUrl({ ...param, page, limit: EXPORT_PAGE_SIZE });
            const response = await axios.get(url);
            const batch = response.data?.data || [];
            rows.push(...batch);
            if (batch.length < EXPORT_PAGE_SIZE) break;
        }

        const header = [
            "Deal ID", "Tenant", "Host", "Property", "Room", "Booking Fee",
            "Status", "Payout Status", "Move In Date", "Latest Release Date",
            "Payout Code", "Paid On",
        ];
        const body = rows.map((r) => [
            r.deal_code, r.tenant_name, r.host_name, r.listing_title, r.room_size || r.room_type,
            r.booking_fee, r.deal_status, r.payout_state,
            csvDate(r.move_in_date), csvDate(r.latest_release_date),
            r.payout_code, csvDate(r.paid_at),
        ]);
        const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const now = new Date();
        link.setAttribute("download", `payouts_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setPayoutExporting(false));
    }
};

// The detail is keyed by deal, not by payout: a booking fee has a page from the
// moment it is held, which is before the host has requested a payout for it.
export const doGetPayoutDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/payout-control/deal/${param.deal_id}`;
        const response = await axios.get(url);
        dispatch(doGetPayoutDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetPayoutDetailFail(error.response));
    }
};

export const doMarkPayoutProcessed = (payoutId, transaction_reference, notes, receiptFile) => async (dispatch) => {
    try {
        const url = `/api/admin/payout-control/${payoutId}/process`;
        const formData = new FormData();
        formData.append("transaction_reference", transaction_reference);
        if (notes) formData.append("notes", notes);
        if (receiptFile) formData.append("receipt", receiptFile);

        const response = await axios.patch(url, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        dispatch(doMarkProcessedSuccess(response.data));
    } catch (error) {
        dispatch(doMarkProcessedFail(error.response));
    }
};

export const {
    doGetPayoutListSuccess, doGetPayoutListFail,
    doGetPayoutStatsSuccess, doGetPayoutStatsFail,
    doGetPayoutDetailSuccess, doGetPayoutDetailFail,
    doMarkProcessedSuccess, doMarkProcessedFail,
    setPayoutExporting,
    resetPayoutControlState, resetPayoutControlDetailState,
} = payoutControlSlice.actions;

export const getPayoutList = (state) => state.payoutControl?.listData;
export const getPayoutListPage = (state) => state.payoutControl?.page;
export const getPayoutListPageCount = (state) => state.payoutControl?.pageCount;
export const getPayoutListTotalRow = (state) => state.payoutControl?.totalRow;
export const getPayoutStats = (state) => state.payoutControl?.statsData;
export const getPayoutExporting = (state) => state.payoutControl?.exporting;
export const getPayoutDetail = (state) => state.payoutControl?.detailData;
export const getPayoutProcessSuccess = (state) => state.payoutControl?.processSuccess;
export const getPayoutErrorDetailData = (state) => state.payoutControl?.errorDetailData;

export default payoutControlSlice.reducer;
